import React from "react";
import {
  Modal,
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function CategoryFormModal({
  open,
  handleClose,
  categoryData, // This now represents subcategory data
  handleInputChange,
  handleFileChange,
  handleSubmit,
  isEditing,
  categories, // For parent category selection
  serviceTypes,
  services,
  customForms,
}) {
  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox
        p={3}
        bgcolor="white"
        borderRadius="lg"
        style={{
          width: 900,
          margin: "50px auto",
          outline: "none",
          backgroundColor: "white",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <MDTypography variant="h6" mb={2}>
          {isEditing ? "Edit Sub Category" : "Add Sub Category"}
        </MDTypography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={serviceTypes}
                getOptionLabel={(option) => option?.name_en || ""}
                value={categoryData.service_type_id ? serviceTypes.find(serviceType => serviceType.id === categoryData.service_type_id) : null}
                onChange={(event, newValue) => {
                  handleInputChange({ target: { name: 'service_type_id', value: newValue ? newValue.id : null } });
                }}
                renderInput={(params) => <TextField {...params} label="Select Service Type" required />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customForms}
                getOptionLabel={(option) => option?.name || ""}
                value={categoryData.form_id ? customForms.find(customForm => customForm.id === categoryData.form_id) : null}
                onChange={(event, newValue) => {
                  handleInputChange({ target: { name: 'form_id', value: newValue ? newValue.id : null } });
                }}
                renderInput={(params) => <TextField {...params} label="Select Form" required />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={categories}
                getOptionLabel={(option) => option?.name_en || ""}
                value={categoryData.parent_id ? categories.filter(category => categoryData.parent_id.includes(category.id)) : []}
                onChange={(event, newValue) => {
                  handleInputChange({
                    target: {
                      name: 'parent_id',
                      value: newValue.map(item => item.id)
                    }
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Categories"
                    error={handleSubmit && (!categoryData.parent_id || categoryData.parent_id.length === 0)}
                    helperText={handleSubmit && (!categoryData.parent_id || categoryData.parent_id.length === 0) ? 'Please select at least one category' : ''}
                  />
                )}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={services}
                getOptionLabel={(option) => option?.name_en || ""}
                value={categoryData.service_ids.map(id => services.find(service => service.id === id)).filter(Boolean)} // Filter out undefined if an ID isn't found
                onChange={(event, newValue) => {
                  handleInputChange({ target: { name: 'service_ids', value: newValue.map(service => service.id) } });
                }}
                renderInput={(params) => <TextField {...params} label="Select Services" variant="outlined" />}
              />
            </Grid>

            {/* Title (EN) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title (EN)"
                name="name_en"
                value={categoryData.name_en}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            </Grid>

            {/* Title (FR) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title (FR)"
                name="name_fr"
                value={categoryData.name_fr}
                onChange={handleInputChange}
                margin="normal"
                required
              />
            </Grid>

            {/* Description (EN) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Description (EN)"
                name="description_en"
                value={categoryData.description_en}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>

            {/* Description (FR) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Description (FR)"
                name="description_fr"
                value={categoryData.description_fr}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Price"
                name="min_price"
                value={categoryData.min_price}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Price Per Km"
                name="price_per_km"
                value={categoryData.price_per_km}
                inputMode="decimal"
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-"].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(event) => {
                  const { value } = event.target;
                  if (value === "" || /^\d*(\.\d*)?$/.test(value)) {
                    handleInputChange({ target: { name: "price_per_km", value } });
                  }
                }}
                margin="normal"
              />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryData.double_location}
                    onChange={(e) => handleInputChange({ target: { name: 'double_location', value: e.target.checked, type: 'checkbox' } })}
                    name="double_location"
                    color="primary"
                  />
                }
                label="Double Location"
              />
            </Grid> */}

            <Grid item xs={12} sm={6}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="file"
                  id="file-input"
                  onChange={(e) => handleFileChange(e, 'image')}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-input">
                  <Button variant="outlined" component="span" style={{ width: '100%', color: 'gray', margin: "20px 0" }}>
                    Upload Image
                  </Button>
                </label>
                {categoryData.image_preview && (
                  <img
                    src={categoryData.image_preview}
                    alt="Preview"
                    style={{ width: '100px', height: 'auto', marginLeft: '10px' }}
                  />
                )}
              </div>
            </Grid>
          </Grid>

          <MDBox mt={3} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{
                whiteSpace: "nowrap",
                color: "white",
              }}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </MDBox>
        </form>
      </MDBox>
    </Modal>
  );
}

export default CategoryFormModal;